import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AuthLayout from './auth/AuthLayout';
import { Banner, Field } from '../design/ui';

// Self-serve signup (backend: POST /api/auth/logistics/signup) for
// logisticians/3PLs and e-merchants: the profile chosen here picks the steps of
// the « Mise en route » (/onboarding), where the new account lands, logged in.
const PROFILES = [
  { key: 'logistician', title: 'Logisticien / 3PL', text: 'J’expédie pour des marques' },
  { key: 'merchant', title: 'E-commerçant', text: 'Je vends en ligne' },
];

const LogisticsSignup = () => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    company_name: '',
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    agent_name: '',
    business_profile: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.business_profile) {
      setError(t('logisticsSignup.profileRequired', 'Choisissez votre activité.'));
      return;
    }
    setIsSubmitting(true);
    try {
      const locale = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
      const response = await axios.post('/api/auth/logistics/signup', {
        ...form,
        agent_name: form.agent_name || null,
        locale,
      });
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('shop', form.company_name);
      window.location.href = '/onboarding';
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : t('logisticsSignup.error', 'Could not create the account. Please check the form and try again.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const input = (field, props = {}) => (
    <input id={`signup-${field}`} className="u-input" value={form[field]} onChange={setField(field)} {...props} />
  );

  return (
    <AuthLayout
      wide
      heading={t('logisticsSignup.title', 'Créer votre compte')}
      footer={<a className="u-link" href="/">{t('logisticsSignup.alreadyAccount', 'Déjà un compte ? Se connecter')}</a>}
    >
      <form className="u-form" onSubmit={handleSignup}>
        {error && <Banner tone="danger">{error}</Banner>}
        <div className="u-field">
          <span className="u-label" id="signup-profile-label">{t('logisticsSignup.profile', 'Votre activité')}</span>
          <div className="auth-choice" role="radiogroup" aria-labelledby="signup-profile-label">
            {PROFILES.map(({ key, title, text }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={form.business_profile === key}
                className={`auth-choice-option ${form.business_profile === key ? 'is-selected' : ''}`}
                onClick={() => { setForm((prev) => ({ ...prev, business_profile: key })); setError(''); }}
              >
                <strong>{t(`logisticsSignup.profiles.${key}.title`, title)}</strong>
                <span>{t(`logisticsSignup.profiles.${key}.text`, text)}</span>
              </button>
            ))}
          </div>
        </div>
        <Field label={t('logisticsSignup.companyName', 'Nom de l’entreprise')} htmlFor="signup-company_name">
          {input('company_name', { type: 'text', maxLength: 120, autoComplete: 'organization', required: true })}
        </Field>
        <div className="u-form-grid">
          <Field label={t('logisticsSignup.firstname', 'Prénom')} htmlFor="signup-firstname">
            {input('firstname', { type: 'text', autoComplete: 'given-name', required: true })}
          </Field>
          <Field label={t('logisticsSignup.lastname', 'Nom')} htmlFor="signup-lastname">
            {input('lastname', { type: 'text', autoComplete: 'family-name', required: true })}
          </Field>
        </div>
        <Field label={t('email', 'E-mail')} htmlFor="signup-email">
          {input('email', { type: 'email', autoComplete: 'email', required: true })}
        </Field>
        <Field label={t('logisticsSignup.phone', 'Téléphone (format international)')} htmlFor="signup-phone_number">
          {input('phone_number', { type: 'tel', placeholder: '+33612345678', autoComplete: 'tel', required: true })}
        </Field>
        <Field label={t('password', 'Mot de passe')} htmlFor="signup-password" help={t('publicUi.passwordRule', '8 caractères minimum.')}>
          {input('password', { type: 'password', minLength: 8, autoComplete: 'new-password', required: true })}
        </Field>
        <Field
          label={t('logisticsSignup.agentName', 'Nom de l’agent IA (facultatif)')}
          htmlFor="signup-agent_name"
        >
          {input('agent_name', { type: 'text', maxLength: 60, placeholder: t('logisticsSignup.agentNamePlaceholder', 'ex. Camille') })}
        </Field>
        <button type="submit" className="u-btn u-btn--primary u-btn--block" disabled={isSubmitting}>
          {isSubmitting ? t('common.loading', 'Chargement…') : t('logisticsSignup.submit', 'Créer mon espace')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default LogisticsSignup;
